import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { GetProfileDetailsPayload } from '@/domains/profileDetails/store/types';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import {
  getProfileDetails,
  getProfileDetailsSuccess,
  setAvatarUploading,
  setNotificationTypes,
  uploadAvatar,
  uploadAvatarSuccess,
} from '@/domains/profileDetails/store/profileDetailsSlice';
import HttpService from '@/services/HttpService/HttpService';
import SnacksService from '@/services/SnackService/SnackService';

function* getEDSProfileDetailsSaga({ payload }: PayloadAction<GetProfileDetailsPayload>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const [{ data: accountData }, { data: notificationTypesData }] = yield all([
      call(
        HttpService.get,
        `/v2/Account/${payload.userId}?includeEntities=Person,AccountNotificationTypes,Person.ContactInformation`,
        {},
        {
          baseURL: import.meta.env.VITE_API_URL_EDS,
        },
      ),
      call(HttpService.post, `/filter/${EdsEntityCodes.NotificationType}`, [], {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      }),
    ]);

    const mappedData = {
      ...accountData,
      accountNotificationTypes: accountData.accountNotificationTypes?.map((notification) => notification.id) || [],
    };

    const normalizedAccountDataResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, mappedData, {
      section: EdsDefinitionSections.ProfileDetails,
    });
    const normalizedNotificationTypesDataResponse: NormalizedV2Response[] = yield call(
      EdsAdapterService.normalize,
      notificationTypesData.listings,
    );

    EdsDebugService.logResponse(EdsEntityCodes.Account, {
      rawResponse: accountData,
      normalizedResponse: normalizedAccountDataResponse,
      includedEntitiesRecord: normalizedAccountDataResponse.entities,
      section: EdsDefinitionSections.ProfileDetails,
      dataBySlot: EdsAdapterService.generateSlotRecord(normalizedAccountDataResponse),
    });

    yield put(getProfileDetailsSuccess(normalizedAccountDataResponse));
    yield put(setNotificationTypes(normalizedNotificationTypesDataResponse));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* uploadAvatarSaga({ payload: { image } }: PayloadAction<{ image: File }>) {
  try {
    yield put(setAvatarUploading(true));

    const form = new FormData();
    form.append('file', image, image?.name);

    const { data } = yield call(HttpService.post, 'Account/upload-file', form, {});

    yield put(uploadAvatarSuccess(data.value));
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to upload avatar.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    console.error(error);
  } finally {
    yield put(setAvatarUploading(false));
  }
}

export function* profileDetailsWatcher() {
  yield takeLatest(getProfileDetails.type, getEDSProfileDetailsSaga);
  yield takeLatest(uploadAvatar.type, uploadAvatarSaga);
}
